import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { fallbackProjects } from '../data/fallbackProjects';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './RealisationsPreview.css';

gsap.registerPlugin(ScrollTrigger);

export default function RealisationsPreview() {
  const [projects, setProjects] = useState(fallbackProjects);
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    async function fetchRecent() {
      try {
        const { data, error } = await supabase
          .from('realisations')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(6);

        if (!error && data && data.length > 0) {
          // Merge custom supabase projects with fallback projects if needed
          setProjects(data);
        } else {
          setProjects(fallbackProjects);
        }
      } catch (err) {
        console.warn('Utilisation des réalisations locales');
        setProjects(fallbackProjects);
      }
    }
    fetchRecent();
  }, []);

  useEffect(() => {
    if (projects.length === 0) return;

    const ctx = gsap.context(() => {
      // Staggered card reveal
      cardsRef.current.forEach((card, i) => {
        if (!card) return;
        gsap.fromTo(card,
          { y: 80, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 90%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [projects]);

  if (projects.length === 0) return null;

  // First project is the "hero" featured one
  const featured = projects[0];
  const rest = projects.slice(1);

  return (
    <section className="port-section" id="portfolio" ref={sectionRef}>
      <div className="port-container">

        {/* Header */}
        <div className="port-header">
          <span className="port-label">Notre Portfolio</span>
          <h2 className="port-title">
            NOS<br />
            <em>RÉALISATIONS.</em>
          </h2>
        </div>

        {/* Featured hero project — now links to project detail page */}
        <Link
          to={`/realisation/${featured.id}`}
          className="port-featured"
          ref={el => (cardsRef.current[0] = el)}
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <div className="port-featured__img-wrap">
            <img src={featured.image_url} alt={featured.title} className="port-featured__img" loading="lazy" />
            <div className="port-featured__overlay" />
          </div>
          <div className="port-featured__content">
            <span className="port-featured__category">
              {featured.category} {featured.city && `· ${featured.city}`}
            </span>
            <h3 className="port-featured__title">{featured.title}</h3>
            <span className="port-featured__cta">Découvrir l'Étude de Cas →</span>
          </div>
        </Link>

        {/* Asymmetric grid — now links to project detail pages */}
        <div className="port-grid">
          {rest.map((project, i) => (
            <Link
              key={project.id}
              to={`/realisation/${project.id}`}
              className={`port-card ${i === 0 || i === 3 ? 'port-card--wide' : ''}`}
              ref={el => (cardsRef.current[i + 1] = el)}
              style={{ textDecoration: 'none', display: 'block' }}
            >
              <div className="port-card__img-wrap">
                <img src={project.image_url} alt={project.title} className="port-card__img" loading="lazy" />
                <div className="port-card__overlay" />
              </div>
              <div className="port-card__content">
                <span className="port-card__category">
                  {project.category} {project.city && `· ${project.city}`}
                </span>
                <h3 className="port-card__title">{project.title}</h3>
                <span className="port-card__arrow">→</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Home page Realisations CTA */}
        <div className="port-cta-wrapper" style={{ display: 'flex', justifyContent: 'center', marginTop: '60px' }}>
          <Link to="/realisations" className="btn btn-primary" style={{ minWidth: '250px' }}>
            Voir plus de réalisations →
          </Link>
        </div>
      </div>
    </section>
  );
}

